import { MixpanelEvent } from '@/services/eventEnums';
import { MixpanelTracking } from '@/services/mixpanel';

import { Box, LinearProgress, Link, List, ListItemText, Stack, Typography } from '@mui/material';
import ProductCardSkeleton from '../skeletons/productCardSkeleton';

import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';

import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import ImageElement from '@arcgis/core/layers/support/ImageElement';
import CornersGeoreference from '@arcgis/core/layers/support/CornersGeoreference';
import { Controller } from 'react-hook-form';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  filtersDefaultValues,
  filterType,
  isPlatformHub,
  LVCardFiltersDefaultValues,
} from '@/constants';

import { areaFormat, cmRangeToMeters, convertStringToDate, filterArchiveData } from '@/lib/utils';
import { Scrollbar } from 'react-scrollbars-custom';
import { PROVIDER_TO_SATELLITES } from './sider.utils';
import { MultipleOverlappingModal } from './Dialog/multipleOverlappingModal';
import { useShoppingCart } from '@/context/ShoppingCartContext';
import { ProductCardType } from './sider.types';
import {
  GraphicsLayer,
  MapContext,
  PreviewLayer,
  SketchViewModelType,
} from '../../context/MapContext/MapProvider';

import K from '@/constants/constants';
import { subscribe, unsubscribe } from '@/lib/events';

import ChipCarousel from './chipCarousel';
import customToasterUpdater from '../toast/CustomToastUpdater';
import NewFilterPopover from './filterDialog/newFilterPopover';
import { ProductListing } from './ProductListing';
import { useTranslation } from 'react-i18next';
import ArchiveLandingScreen from './archiveLandingScreen';
import MultiProductAccordion from './multiProductAccordion';

export default function ExploreExisting({
  quickSketchVM,
  intersectLayer,
  previewLayer,
  sketchVM,
  polygonCoordinates,
  products,
  formControls,
  getWebSocketPayload,
  searchBarComponentProps,
  setIsArchiveLandingScreen,
  checkArea,
  setFilterLastDate,
  setFilterStartDate,
  filterLastDate,
  filterStartDate,
  isFetchingData,
  progress,
  showSkeleton,
  searchValue,
  productCardContainerRef,
  removeLayerAndHighlightedCard,
  searchData,
  setPolygonCoordinates,
  isArchiveLandingScreen,
  isArchiveLandingScreenRef,
  handleFileUpload,
  selectDataType,
  setSelectDataType,
  placeholder,
  selectDataTypeRef,
}: {
  quickSketchVM: SketchViewModelType;
  intersectLayer: GraphicsLayer;
  previewLayer: PreviewLayer;
  sketchVM: SketchViewModelType;
  polygonCoordinates: number[][][];
  searchBarComponentProps: any;
  getWebSocketPayload: any;
  setIsArchiveLandingScreen: any;
  products: ProductCardType[];
  formControls: any;

  setFilterStartDate: React.Dispatch<React.SetStateAction<string>>;
  setFilterLastDate: React.Dispatch<React.SetStateAction<string>>;
  filterStartDate: string;
  filterLastDate: string;
  isFetchingData: boolean;
  progress: number;
  checkArea: any;
  showSkeleton: boolean;
  searchValue: string;
  productCardContainerRef: React.RefObject<HTMLDivElement>;
  removeLayerAndHighlightedCard: () => void;
  searchData: {
    source?: string | null;
    from?: string | null;
    to?: string | null;
    coordinates?: any | null;
    resolution?: string | null;
    valid: boolean;
  };
  setPolygonCoordinates: React.Dispatch<React.SetStateAction<number[][][]>>;
  isArchiveLandingScreen: boolean;
  isArchiveLandingScreenRef: React.MutableRefObject<boolean>;
  handleFileUpload: any;

  selectDataType: any;
  setSelectDataType: any;
  placeholder: boolean;
  selectDataTypeRef: any;
}) {
  const { drawRectangleHandlerRef } = useContext(MapContext);
  const [filters, setFilters] = useState<filterType>(filtersDefaultValues);
  const [showDateRangeNote, setShowDateRangeNote] = useState<any>(false);
  const [archiveFiltersElements, setArchiveFiltersElements] = useState([]);

  const viewportRef = useRef<any>(null);

  const {
    setIsNew,

    tabValue,
    multiProduct,
    setMultiProduct,
    setOpenPreviewInfoPopup,
    archiveUiElementsRef,
    taskingUiElementsRef,
  } = useShoppingCart(); // getting the context methods

  const previewRefMap = useRef<any>({});
  const { t } = useTranslation('orderImagery');

  const { control, setValue, getValues, errors } = formControls;

  const getCloudCoverageValueForExperiment = (updatedValue?: any) => {
    if (isPlatformHub) {
      return [0, 100];
    }
    const dataTypes = updatedValue || selectDataTypeRef?.current;
    // Set Cloud Coverage Value 0-25
    const hasSAR = dataTypes.includes('sar');
    const hasBasemap = dataTypes.includes('basemap');
    const isSingleType = dataTypes.length === 1;
    const isSAROrBasemap = hasSAR || hasBasemap;

    // Case 1: Single Datatype selected and not sar/basemap -> ['0-25']
    if (isSingleType && !isSAROrBasemap) {
      return [0, 25];
    }

    // Case 2: Single datatype selected and is sar/basemap -> []
    if (isSingleType && isSAROrBasemap) {
      return [0, 100];
    }

    // Case3: More than one Datatype selected -> ['0-25']
    if (dataTypes.length > 1) {
      return [0, 25];
    }

    return [0, 100];
  };
  useEffect(() => {
    const searchParamFilterValues = { ...filters };

    if ((searchData.resolution || searchData?.source || searchData?.coordinates) && sketchVM) {
      if (selectDataTypeRef.current?.includes('all')) {
        const allSelectedOptionsData = archiveUiElementsRef?.current?.data?.data_types?.filter(
          (item: any) => selectDataTypeRef.current?.includes(item?.value)
        );
        setArchiveFiltersElements(allSelectedOptionsData?.[0]?.filters);
      }

      //creating new array because we did shallow copy above so the arrays inside object is passed by reference
      if (searchData?.resolution) {
        // const updatedResolutionValue = urlResolutionToFilterResolutionConversion(
        //   searchData.resolution
        // );
        const updatedResolutionValue = cmRangeToMeters(searchData?.resolution);

        searchParamFilterValues.resolution = updatedResolutionValue;
      }
      if (searchData?.source) {
        searchParamFilterValues.source = [searchData.source];
      }
      // searchParamFilterValues.resolution = [searchData?.resolution]

      isArchiveLandingScreenRef.current = false;
    }

    const cloudCoverageValue = getCloudCoverageValueForExperiment();
    // 1. if search params (source / resolution) are present set those values
    // 2. Set cloud coverage value according to feature flag variant
    if (searchData.resolution || searchData?.source) {
      setFilters({
        ...searchParamFilterValues,
        cloud_coverage: cloudCoverageValue,
      });
    } else {
      setFilters((prev: any) => {
        return {
          ...prev,
          cloud_coverage: cloudCoverageValue,
        };
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sketchVM, searchData, isArchiveLandingScreenRef, archiveUiElementsRef?.current]);

  useEffect(() => {
    const dataTypes = archiveUiElementsRef?.current?.data?.data_types || [];
    const selected = selectDataTypeRef.current || [];

    const allOption = dataTypes.find((item: any) => item?.value === 'all');

    // When one data type is selected → return its filters
    if (selected.length === 1) {
      const selectedOption = dataTypes.find((item: any) => item?.value === selected[0]);

      setArchiveFiltersElements(selectedOption?.filters || allOption?.filters);
      return;
    }

    // When multiple or zero are selected → return "all" filters
    setArchiveFiltersElements(allOption?.filters);

    // if (selectDataTypeRef.current?.includes('all')) {
    //   setArchiveFiltersElements(allSelectedOptionsData?.[0]?.filters);
    // }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArchiveLandingScreen]);

  // Listen for AI agent search refinement events (filter, sort, clear)
  useEffect(() => {
    const handleAgentRefine = (event: any) => {
      const { action, filters: newFilters, sort_by } = event.detail || {};

      if (action === 'clear_filters') {
        setFilters(filtersDefaultValues);
        setSelectDataType(['all']);
        selectDataTypeRef.current = ['all'];
        return;
      }

      if (action === 'apply_filters' && newFilters) {
        // Resolve AI source names to exact product source values.
        // 1. Expand provider names via mapping (e.g., "Airbus" → ["Pleiades", "Spot"])
        // 2. Fuzzy-match against actual product sources (e.g., "Landsat" → "Landsat-8")
        let resolvedSource: string[] | undefined;
        if (newFilters.source) {
          const productSources = Array.from(new Set(products.map(p => p.source)));
          const expanded: string[] = [];

          for (const aiSource of newFilters.source as string[]) {
            const providerSats = PROVIDER_TO_SATELLITES[aiSource.toLowerCase()];
            if (providerSats) {
              // Provider name → expand to all its satellite names
              expanded.push(...providerSats);
            } else {
              expanded.push(aiSource);
            }
          }

          // Match expanded names against actual product sources via substring
          resolvedSource = productSources.filter(ps =>
            expanded.some(
              ex =>
                ps.toLowerCase().includes(ex.toLowerCase()) ||
                ex.toLowerCase().includes(ps.toLowerCase())
            )
          );
          if (resolvedSource.length === 0) resolvedSource = expanded;
        }

        setFilters(prev => ({
          ...prev,
          ...(newFilters.resolution !== undefined && { resolution: newFilters.resolution }),
          ...(newFilters.cloud_coverage !== undefined && {
            cloud_coverage: newFilters.cloud_coverage,
          }),
          ...(newFilters.area_coverage !== undefined && {
            area_coverage: newFilters.area_coverage,
          }),
          ...(resolvedSource !== undefined && { source: resolvedSource }),
          ...(newFilters.sensor_mode !== undefined && { sensor_mode: newFilters.sensor_mode }),
          ...(newFilters.looks !== undefined && { looks: newFilters.looks }),
          ...(newFilters.subcategory !== undefined && { subcategory: newFilters.subcategory }),
          ...(newFilters.dem !== undefined && { dem: newFilters.dem }),
          ...(newFilters.bands !== undefined && { bands: newFilters.bands }),
        }));
      }

      if (action === 'sort' && sort_by) {
        setFilters(prev => ({ ...prev, sort_by }));
      }
    };

    subscribe('agent:refine-search', handleAgentRefine);
    return () => unsubscribe('agent:refine-search', handleAgentRefine);
  }, [products, setSelectDataType, selectDataTypeRef]);

  const polygonArea = useMemo(() => {
    let area = geometryEngine.geodesicArea(
      new Polygon({ rings: polygonCoordinates }),
      'square-kilometers'
    );
    if (area < 0) area *= -1;

    return area;
  }, [polygonCoordinates]);

  //multi product dialog ref
  const overlapDialogRef = useRef<{ click1: () => void; click2: () => void }>({
    click1: () => {},
    click2: () => {},
  });

  function handleOnMouseEnterDraw(coordinates: number[][][]) {
    // let { polygon } = getPolygonAndNormalizeCoordsForPreview(coordinates);
    // Construct the polygon from coordinates
    const polygon = new Polygon({
      rings: coordinates,
      spatialReference: { wkid: 4326 },
    });
    const graphic = new Graphic({ geometry: polygon, symbol: quickSketchVM.polygonSymbol.clone() });

    quickSketchVM.layer.removeAll();
    quickSketchVM.layer.add(graphic);
  }

  function handleOnMouseLeaveRemove() {
    quickSketchVM.layer.removeAll();
  }

  function handleOnClickRemovePreviewImage(id: string) {
    //clear or remove the old intersect graphic

    const totalImagePreviews = previewLayer.source.elements.length;
    if (totalImagePreviews === 1) {
      setOpenPreviewInfoPopup(false);
    }
    if (previewRefMap.current[id]) {
      previewRefMap.current[id]();
      delete previewRefMap.current[id];
    }
  }

  function getPolygonAndNormalizeCoordsForPreview(coordinates: number[][][], bbox: number[]) {
    let coords;
    var box = {} as { xmin: number; ymin: number; xmax: number; ymax: number };

    // Check if coordinates are bbox style or points array
    if (bbox && bbox.length != 0) {
      const [minX, minY, maxX, maxY] = bbox;

      coords = coordinates;

      box.xmin = minX;
      box.xmax = maxX;
      box.ymin = minY;
      box.ymax = maxY;
    } else {
      // Assuming it's already a formatted polygon point array
      // const box = (coordinates as unknown as number[][][])[0].slice(0, -1);

      coords = coordinates;
    }

    // Construct the polygon from coordinates
    const polygon = new Polygon({
      rings: coords,
      spatialReference: { wkid: 4326 },
    });

    return { polygon, box };
  }

  function handleOnClickPreviewImage(
    coordinates: number[][][],
    intersectionCoordinates: number[][][] | null,
    bbox: number[],
    url: string,
    area: number,
    id: string
  ) {
    // Remove all existing images from the layer
    // previewLayer.source.elements.removeAll();
    let { polygon, box } = getPolygonAndNormalizeCoordsForPreview(coordinates, bbox);

    //the drawn polygon coordinates
    const AOIpolygon = new Polygon({
      rings: polygonCoordinates,
      spatialReference: { wkid: 4326 },
    });

    //get the intersection of image polygon and drawn polygon

    const intersectPolygon = intersectionCoordinates
      ? new Polygon({
          rings: intersectionCoordinates,
          spatialReference: { wkid: 4326 },
        })
      : geometryEngine.intersect(polygon, AOIpolygon);

    //create the graphic
    const graphic = new Graphic({
      geometry: Array.isArray(intersectPolygon) ? intersectPolygon[0] : intersectPolygon,
      attributes: { url },
      symbol: quickSketchVM.polygonSymbol.clone(),
    });

    //clear or remove the old graphic
    // intersectLayer.removeAll();

    //create the new graphic
    intersectLayer.add(graphic);

    sketchVM.emit('update', { state: 'showIntersectArea', area: areaFormat(area) });

    // Create an image element
    const imgElement = new ImageElement({
      image: url,

      georeference: new CornersGeoreference({
        topLeft: {
          x: box?.xmin
            ? box.xmin
            : coordinates[0].length === 5
              ? polygon.rings[0][0][0]
              : polygon.extent.xmin,
          y: box?.ymax
            ? box.ymax
            : coordinates[0].length === 5
              ? polygon.rings[0][0][1]
              : polygon.extent.ymax,
          spatialReference: { wkid: 4326 },
        },
        topRight: {
          x: box?.xmax
            ? box.xmax
            : coordinates[0].length === 5
              ? polygon.rings[0][1][0]
              : polygon.extent.xmax,
          y: box?.ymax
            ? box.ymax
            : coordinates[0].length === 5
              ? polygon.rings[0][1][1]
              : polygon.extent.ymax,
          spatialReference: { wkid: 4326 },
        },
        bottomRight: {
          x: box?.xmax
            ? box.xmax
            : coordinates[0].length === 5
              ? polygon.rings[0][2][0]
              : polygon.extent.xmax,
          y: box?.ymin
            ? box.ymin
            : coordinates[0].length === 5
              ? polygon.rings[0][2][1]
              : polygon.extent.ymin,
          spatialReference: { wkid: 4326 },
        },

        bottomLeft: {
          x: box?.xmin
            ? box.xmin
            : coordinates[0].length === 5
              ? polygon.rings[0][3][0]
              : polygon.extent.xmin,
          y: box?.ymin
            ? box.ymin
            : coordinates[0].length === 5
              ? polygon.rings[0][3][1]
              : polygon.extent.ymin,
          spatialReference: { wkid: 4326 },
        },
      }),
    });

    // Add the image element to the layer
    previewLayer.source.elements.add(imgElement);

    // add the removing function
    (previewRefMap.current as any)[id] = () => {
      intersectLayer.remove(graphic);
      previewLayer.source.elements.remove(imgElement);
    };

    const isPreviewInfoClosedByUser = sessionStorage.getItem('previewInfoClosedByUser');
    if (isPreviewInfoClosedByUser === null) {
      sessionStorage.setItem('previewInfoClosedByUser', 'false');
      setOpenPreviewInfoPopup(true);
      MixpanelTracking.getInstance().track(
        MixpanelEvent.SHOW_PREVIEW_RESOLUTION_INFO_BOX_UNDER_AOI
      );
    }
    if (isPreviewInfoClosedByUser === 'false') {
      setOpenPreviewInfoPopup(true);
      MixpanelTracking.getInstance().track(
        MixpanelEvent.SHOW_PREVIEW_RESOLUTION_INFO_BOX_UNDER_AOI
      );
    }
  }

  const filteredProducts = useMemo(() => {
    return products.length > 0 ? filterArchiveData(products, filters) : products;
  }, [filters, products]);

  // Sorting the Data
  const sortedProducts = useMemo(() => {
    return filteredProducts.length > 0
      ? filteredProducts.sort((a: ProductCardType, b: ProductCardType) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (filters?.sort_by === K.ARCHIVE_FILTERS.SORT_BY.DATE_DESC) {
            return dateB.getTime() - dateA.getTime();
          } else if (filters?.sort_by === K.ARCHIVE_FILTERS.SORT_BY.DATE_ASC) {
            return dateA.getTime() - dateB.getTime();
          } else if (filters?.sort_by === K.ARCHIVE_FILTERS.SORT_BY.PRICE_ASC) {
            return a.price - b.price;
          } else if (filters?.sort_by === K.ARCHIVE_FILTERS.SORT_BY.PRICE_DESC) {
            return b.price - a.price;
          } else if (filters?.sort_by === K.ARCHIVE_FILTERS.SORT_BY.CLOUD_COVERAGE_ASC) {
            return a.cloudCoverage - b.cloudCoverage;
          } else return 0;
        })
      : [];
  }, [filteredProducts, filters]);

  // stop skeleton when there is any filter out product data otherwise keep original skeleton state
  const productListingSkeleton = sortedProducts?.length > 0 ? false : showSkeleton;
  const productListingProps = useMemo(() => {
    return {
      sortedProducts,
      handleOnMouseEnterDraw,
      handleOnMouseLeaveRemove,
      handleOnClickPreviewImage,
      handleOnClickRemovePreviewImage,
      previewRefMap,
      previewLayer,
      polygonCoordinates,
      multiProduct,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedProducts, multiProduct, polygonCoordinates]);

  const dtIconMapper: any = {
    'all-dt-icon': <CollectionsOutlinedIcon />,
    'satellite-icon': <SatelliteAltOutlinedIcon />,
    'aerial-icon': <i className="icon-aerial"></i>,
    'elevation-icon': <i className="icon-elevation"></i>,
    'sar-icon': <i className="icon-sar"></i>,
    'basemap-dt-icon': <i className="icon-basemap"></i>,
  };

  const renderDtIcon = (icon: string) => {
    return dtIconMapper[icon];
  };

  function getDateRangeBounds(ranges: any) {
    if (!ranges || ranges.length === 0) return null;

    const hasNestedArray = Array.isArray(ranges?.[0]);
    let earliestStart: any = hasNestedArray
      ? ranges?.[0]?.[0]
      : convertStringToDate(ranges?.[0])?.startDate;
    let latestEnd: any = hasNestedArray
      ? ranges?.[0]?.[1]
      : convertStringToDate(ranges?.[0])?.endDate;

    ranges.forEach((date: any) => {
      let start: any = '';
      let end: any = '';
      if (Array.isArray(date)) {
        (start = date[0]), (end = date[1]);
      } else {
        start = convertStringToDate(date)?.startDate;
        end = convertStringToDate(date)?.endDate;
      }

      if (moment(start).isBefore(earliestStart)) earliestStart = start;
      if (moment(end).isAfter(latestEnd)) latestEnd = end;
    });

    return {
      startDate: earliestStart,
      endDate: latestEnd,
    };
  }

  const toggleSelectDataType = (dtData: any, isChipsFilter?: boolean) => {
    let value = dtData?.value;

    let updatedValue: any = [];
    if (value === 'all') {
      updatedValue = ['all'];
    } else {
      const newSelection = selectDataTypeRef.current.includes(value)
        ? selectDataTypeRef.current.filter((item: any) => item !== value) // Deselect
        : [...selectDataTypeRef.current.filter((item: any) => item !== 'all'), value]; // Select

      // If all other options are deselected, fallback to "All"
      updatedValue = newSelection.length === 0 ? ['all'] : newSelection;
      // setSelected(newSelection.length === 0 ? ["All"] : newSelection);
    }

    const allSelectedOptionsData = archiveUiElementsRef?.current?.data?.data_types?.filter(
      (item: any) => updatedValue?.includes(item?.value)
    );

    const filtersForAllOptionOnly = archiveUiElementsRef?.current?.data?.data_types?.filter(
      (item: any) => item?.value === 'all'
    );

    const allSelectedDateRanges = allSelectedOptionsData?.map((item: any) => {
      return item?.dateRange;
    });
    if (allSelectedOptionsData?.length === 1) {
      setArchiveFiltersElements(allSelectedOptionsData[0]?.filters);
    } else if (allSelectedOptionsData?.length > 1) {
      setArchiveFiltersElements(filtersForAllOptionOnly?.[0]?.filters);
    }
    const cloudCoverageValue: any = getCloudCoverageValueForExperiment(updatedValue);

    // CD-1261 set area coverage value  for "Optical-Aerial"  to 25% to 100%
    const areaCoverageValue =
      updatedValue.length === 1 && updatedValue[0] === 'aerial' ? [25, 100] : [0, 100];

    setFilters({
      ...LVCardFiltersDefaultValues,
      area_coverage: areaCoverageValue,
      cloud_coverage: cloudCoverageValue,
    });

    if (!isChipsFilter) {
      const filterDate = getDateRangeBounds(allSelectedDateRanges);
      if (filterDate) {
        setShowDateRangeNote(false);
        setFilterStartDate(filterDate?.startDate);
        setFilterLastDate(filterDate?.endDate);
        setValue('startDate', filterDate?.startDate);
        setValue('lastDate', filterDate?.endDate);
      }
    }

    selectDataTypeRef.current = updatedValue;
    setSelectDataType(updatedValue);
    if (isChipsFilter) {
      MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_EXISTING_DATA_TYPE_FILTER, {
        data_type: updatedValue,
        start_date: filterStartDate,
        end_date: filterLastDate,
        coordinates: polygonCoordinates[0],
        location: searchValue,
      });
    }
  };

  const isFiltersApplied = (data: any): boolean => {
    return !!(
      data?.resolution?.length > 0 ||
      data?.area_coverage?.[0] !== 0 ||
      data?.area_coverage?.[1] !== 100 ||
      data?.cloud_coverage?.[0] !== 0 ||
      data?.cloud_coverage?.[1] !== 100 ||
      data?.source?.length > 0 ||
      data?.bands?.length > 0 ||
      data?.looks?.length > 0 ||
      data?.dem?.length > 0 ||
      data?.sensor_mode?.length > 0 ||
      -data?.subcategory?.length > 0 ||
      (data?.sort_by !== '' && data?.sort_by !== 'Date: newest to oldest') ||
      data?.min_off_nadir_angle > 0 ||
      data?.max_off_nadir_angle < 60 ||
      data?.dataType?.length > 0
    );
  };

  return (
    <Box
      className={`explore-existing ${selectDataTypeRef.current && selectDataTypeRef.current?.length === 1 && selectDataTypeRef.current?.includes('optical') && isPlatformHub ? 'satellite-dt' : 'other-dt'}`}
    >
      {!isArchiveLandingScreen && (
        <>
          <Box className="chips-filters">
            <Box display="flex" gap="4px">
              <NewFilterPopover
                removeLayerAndHighlightedCard={removeLayerAndHighlightedCard}
                filters={filters}
                setFilters={setFilters}
                coordinates={polygonCoordinates}
                results={sortedProducts}
                originalData={products}
                previewRefMap={previewRefMap}
                searchData={searchData}
                isArchiveNewDesignEnabled={true}
                archiveFiltersElements={archiveFiltersElements}
                selectDataTypeRef={selectDataTypeRef}
              />

              <ChipCarousel
                options={archiveUiElementsRef?.current?.data?.data_types}
                selectDataTypeRef={selectDataTypeRef}
                onClickHandler={toggleSelectDataType}
              />
            </Box>
            <Box className="filter-box">
              <MultipleOverlappingModal
                setMultiProduct={setMultiProduct}
                ref={overlapDialogRef}
                removeLayerAndHighlightedCard={removeLayerAndHighlightedCard}
              />
              <Stack
                direction="row"
                spacing={1}
                className="datepicker-wrapper existing-datepicker"
                sx={{ paddingRight: '10px' }}
              >
                <Controller
                  control={control}
                  name="startDate"
                  defaultValue={filterStartDate}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        className="filled-datepicker"
                        value={field.value ? moment(field.value) : moment(filterStartDate)}
                        disableFuture
                        slotProps={{
                          textField: {
                            inputProps: { disabled: true }, // Disable typing
                          },
                        }}
                        onChange={(value: any) => {
                          const newStartDate = moment(value).format('YYYY-MM-DD');
                          field.onChange(newStartDate);
                          const newLastDate = getValues('lastDate');
                          if (
                            moment(newLastDate).isAfter(newStartDate) &&
                            !moment(newLastDate).isSame(newStartDate)
                          ) {
                            setFilterStartDate(newStartDate);
                          } else {
                            customToasterUpdater(t('common:selectDateRange'), 'valid-st');
                          }
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
                <Controller
                  name="lastDate"
                  control={control}
                  defaultValue={filterLastDate}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        className="filled-datepicker"
                        value={field.value ? moment(field.value) : moment(filterLastDate)}
                        slotProps={{
                          textField: {
                            inputProps: { disabled: true }, // Disable typing
                          },
                        }}
                        disableFuture
                        onChange={(value: any) => {
                          const newLastDate = moment(value).format('YYYY-MM-DD');
                          const newStartDate = getValues('startDate');
                          field.onChange(newLastDate);
                          if (
                            moment(newLastDate).isAfter(newStartDate) &&
                            !moment(newLastDate).isSame(newStartDate)
                          ) {
                            setFilterLastDate(newLastDate);
                          } else {
                            customToasterUpdater(t('common:selectDateRange'), 'valid-ed');
                          }
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
              </Stack>
            </Box>
          </Box>
          <Scrollbar className="panel-content-scroll scroll-thumb-color scroll-thumb-color">
            <Box className="panel-content" ref={productCardContainerRef}>
              {isFetchingData ? (
                <LinearProgress
                  className="ctv-linear-progress"
                  aria-label="linear-loader"
                  variant="determinate"
                  value={progress}
                  hidden={!isFetchingData}
                />
              ) : null}
              {products?.length > 0 && archiveUiElementsRef.current && sortedProducts.length > 0 ? (
                <ProductListing {...productListingProps} />
              ) : productListingSkeleton ? (
                <ProductCardSkeleton />
              ) : (
                <Box className="empty-cta" sx={{ height: 'calc(100vh - 492px)' }}>
                  {isFiltersApplied(filters) ? (
                    <Typography>
                      {t(
                        'noResultAfterFiters',
                        'No results found for the applied filters. Try adjusting the filters.'
                      )}
                    </Typography>
                  ) : (
                    <Stack direction="column" alignItems="center">
                      <Typography variant="body2">
                        {t(
                          'noExistingImageWithinDate',
                          'No images are available for this location within the specified date range. Please expand the date range to increase the likelihood of finding results. We recommend the following date ranges for the best chance of retrieving images.'
                        )}
                      </Typography>
                      <List>
                        {archiveUiElementsRef.current?.data?.data_types
                          ?.filter((item: any) => selectDataTypeRef?.current?.includes(item?.value))
                          ?.map((childItem: any) => {
                            return (
                              <ListItemText
                                key={childItem?.value}
                                secondary={
                                  <Typography
                                    color="secondary"
                                    sx={{ textAlign: 'left !important' }}
                                    variant="body2"
                                  >
                                    <b>{`${childItem?.label}: `}</b>
                                    {childItem?.date_range_note}
                                  </Typography>
                                }
                              />
                            );
                          })}
                      </List>

                      <Link
                        onClick={() => {
                          setIsNew('2');

                          const obj = MixpanelTracking.getInstance().getProperty('currentSearchId');

                          MixpanelTracking.getInstance().track(
                            MixpanelEvent.EXPLORE_EXISTING_ORDER_A_NEW_IMAGE_LINK_CLICK,
                            {
                              search_id: obj ? obj?.searchId : null,
                            }
                          );
                        }}
                      >
                        {t('orderANewImage')}
                      </Link>
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          </Scrollbar>

          {isPlatformHub &&
          selectDataTypeRef.current &&
          selectDataTypeRef.current?.length === 1 &&
          selectDataTypeRef.current?.includes('optical') ? (
            <MultiProductAccordion
              multiProduct={multiProduct}
              polygonArea={polygonArea}
              overlapDialogRef={overlapDialogRef}
            />
          ) : null}
          <Typography className="bottom-text">
            {t('notFinding')}
            <Link
              className="ml-1"
              onClick={() => {
                setIsNew('2');
                tabValue.current = '2';
                const obj = MixpanelTracking.getInstance().getProperty('currentSearchId');
                MixpanelTracking.getInstance().track(
                  MixpanelEvent.EXPLORE_EXISTING_ORDER_A_NEW_IMAGE_LINK_CLICK,
                  {
                    search_id: obj ? obj?.searchId : null,
                  }
                );
              }}
            >
              {t('orderANewImage')}
            </Link>
          </Typography>
        </>
      )}

      {/* Archive Landing Screen */}

      {isArchiveLandingScreen && (
        <ArchiveLandingScreen
          drawRectangleHandlerRef={drawRectangleHandlerRef}
          handleFileUpload={handleFileUpload}
          archiveUiElementsRef={archiveUiElementsRef}
          selectDataTypeRef={selectDataTypeRef}
          toggleSelectDataType={toggleSelectDataType}
          formControls={formControls}
          filterStartDate={filterStartDate}
          setShowDateRangeNote={setShowDateRangeNote}
          setFilterLastDate={setFilterLastDate}
          filterLastDate={filterLastDate}
          setFilterStartDate={setFilterStartDate}
          showDateRangeNote={showDateRangeNote}
          renderDtIcon={renderDtIcon}
        />
      )}
    </Box>
  );
}
